import type { Resource } from '__types__';
import type { ReduxProxy } from 'v5/api/_common/reduxProxy';
import { RemoveLinkResponse, ResourceApi } from 'v5/api/resource/resource.api';
import { buildCreateBody } from 'v5/core/resource-write/buildCreateBody';
import { buildUpdateBody } from 'v5/core/resource-write/buildUpdateBody';
import { rowLevelValidation } from 'v5/platform/row-level-validation';
import { WithServices } from 'v5/platform/ts/services';
import { WithErrorService } from 'v5/services/front/ErrorService/ErrorService';
import { SystemEffect } from 'v5/services/internal/MessageService/_private/SystemEffectMessage';
import { WithMessageService } from '../../internal/MessageService/MessageService';
import {
  RemoveLinkResult,
  ResourceMutationService
} from './ResourceMutationService';

export function createResourceMutationService(
  config: WithServices<[WithMessageService, WithErrorService]> & {
    api: ResourceApi;
    reduxProxy: Pick<ReduxProxy, 'write'>;
  }
): ResourceMutationService {
  const {
    api,
    reduxProxy,
    services: { messageService, errorService }
  } = config;
  const interceptor = errorService.withCategory('ResourceMutationService');

  async function reportEffectAndWaitUntilCacheReady(
    scope: SystemEffect[] = []
  ) {
    await messageService.reportSystemEffect.sendRequest(
      {
        scope
      },
      3000
    );
  }
  /**
   * Write-through after a successful graph write: the v1 store gets the
   * v1-shaped copy, the returned contact goes to the v5 item cache at once
   * (`reportCache`), and the system effect refreshes the v5 lists in the
   * background so the form does not wait for it. The v5 side never relies on
   * the v1 action for its own invalidation.
   */
  function publishWrittenResource(
    result: Resource,
    type: 'update' | 'create',
    request: unknown
  ) {
    const { enterprise_workspaces: _membership, ...v1Resource } = result;
    reduxProxy.write.emit({
      type:
        type === 'update' ? 'UPDATE_CONTACT_SUCCESS' : 'CREATE_CONTACT_SUCCESS',
      result: v1Resource,
      // As on v1 actions: callouts reads `body.applicants` off every create.
      body: request
    });
    messageService.reportCache.emit({
      entities: [{ entity: 'resource', payload: result }]
    });
    void reportEffectAndWaitUntilCacheReady([
      { entity: 'resource', entityId: result.id, payload: { type: 'update' } }
    ]).catch(() => undefined);
  }

  async function removeLink(
    response: RemoveLinkResponse,
    askedIds: Resource['id'][]
  ): Promise<RemoveLinkResult> {
    const notLinked = new Set(response.not_linked_ids);
    const removedIds = askedIds.filter(id => !notLinked.has(id));
    if (removedIds.length > 0) {
      await reportEffectAndWaitUntilCacheReady(
        removedIds.map(id => ({
          entity: 'resource' as const,
          entityId: id,
          payload: { type: 'update' as const }
        }))
      );
    }
    return {
      removedCount: response.removed_count,
      notLinkedIds: response.not_linked_ids
    };
  }

  return {
    apply: interceptor.wrap(async (resourceId, patch) => {
      const current = await api.getItem(resourceId);
      const body = buildUpdateBody(patch, current);
      if (body.type === 'no-op') {
        return current;
      }
      // Request rows (`{ id }` stubs, tags without id) aren't `Resource`
      // shapes; `updateItem` stays typed as `Partial<Resource>` for v1 callers.
      const data = body.body as unknown as Partial<Resource>;
      const result = await api.updateItem(resourceId, data);
      publishWrittenResource(result, 'update', data);
      return result;
    }),
    create: interceptor.wrap(async input => {
      const body = buildCreateBody(input);
      const result = await api.createItem(body);
      publishWrittenResource(result, 'create', body);
      return result;
    }),
    updateResource: interceptor.wrap(async request => {
      const result = await api.updateItem(request.id, request.data);

      reduxProxy.write.emit({ type: 'UPDATE_CONTACT_SUCCESS', result });

      await reportEffectAndWaitUntilCacheReady([
        {
          entity: 'resource',
          entityId: request.id,
          payload: { type: 'update' }
        }
      ]);

      return result;
    }),
    assignResourcesToWorkspaces: interceptor.wrap(async request => {
      await api.assignResourcesToWorkspaces({
        ids: request.ids,
        enterprise_workspace_ids: request.enterprise_workspace_ids
      });

      // Invalid contacts are left to the refetch the effect below triggers.
      const resources = rowLevelValidation.validDataFromRows(
        await api.getItems(request.ids)
      );
      const membershipIds = [...request.enterprise_workspace_ids];

      for (const resource of resources) {
        // v1 contact PUT must not receive flat membership ids from redux
        const { enterprise_workspaces: _membership, ...v1Resource } = resource;
        reduxProxy.write.emit({
          type: 'UPDATE_CONTACT_SUCCESS',
          result: v1Resource
        });
      }

      messageService.reportCache.emit({
        entities: resources.map(resource => ({
          entity: 'resource' as const,
          payload: {
            ...resource,
            enterprise_workspaces: membershipIds.map(id => ({
              enterprise_workspace_id: id
            }))
          }
        }))
      });

      await reportEffectAndWaitUntilCacheReady(
        request.ids.map(id => ({
          entity: 'resource',
          entityId: id,
          payload: { type: 'update' }
        }))
      );
    }),
    joinWorkspace: interceptor.wrap(async request => {
      const { joined_ids: joined } = await api.joinWorkspace({
        ids: request.ids,
        enterprise_workspace_id: request.workspaceId
      });
      if (joined.length === 0) return;
      const resources = rowLevelValidation.validDataFromRows(
        await api.getItems(joined)
      );
      for (const resource of resources) {
        // v1 contact PUT must not receive flat membership ids from redux
        const { enterprise_workspaces: _membership, ...v1Resource } = resource;
        reduxProxy.write.emit({
          type: 'UPDATE_CONTACT_SUCCESS',
          result: v1Resource
        });
      }
      messageService.reportCache.emit({
        entities: resources.map(resource => ({
          entity: 'resource' as const,
          payload: resource
        }))
      });
      await reportEffectAndWaitUntilCacheReady(
        joined.map(id => ({
          entity: 'resource',
          entityId: id,
          payload: { type: 'update' }
        }))
      );
    }),
    removeFromOrganisation: interceptor.wrap(async request =>
      removeLink(
        await api.removeFromOrganisation({
          ids: request.ids,
          organisation_id: request.organisationId
        }),
        request.ids
      )
    ),
    removeFromAgent: interceptor.wrap(async request =>
      removeLink(
        await api.removeFromAgent({
          ids: request.ids,
          agent_id: request.agentId
        }),
        request.ids
      )
    ),
    archiveResources: interceptor.wrap(async request => {
      try {
        await api.bulkArchive(request);

        // Report system effects for successfully archived resources

        await reportEffectAndWaitUntilCacheReady(
          request.ids.map(id => ({
            entity: 'resource',
            entityId: id,
            payload: { type: 'update' }
          }))
        );
      } catch (error) {
        console.error('Failed to archive resources:', error);
        throw new Error('Failed to archive resources');
      }
    }),

    unarchiveResources: interceptor.wrap(async request => {
      try {
        await api.bulkUnarchive(request);

        // Report system effects for successfully unarchived resources

        await reportEffectAndWaitUntilCacheReady(
          request.ids.map(id => ({
            entity: 'resource',
            entityId: id,
            payload: { type: 'update' }
          }))
        );
      } catch (error) {
        console.error('Failed to unarchive resources:', error);
        throw new Error('Failed to unarchive resources');
      }
    }),

    createPin: interceptor.wrap(async request => {
      await api.createPin(request);
      // No v1 action: its reducer prepends to the contact's loaded pins and
      // throws for a contact whose associations were never fetched.
      void reportEffectAndWaitUntilCacheReady([
        {
          entity: 'resource',
          entityId: request.resourceId,
          payload: { type: 'update' }
        }
      ]).catch(() => undefined);
    })
  };
}
